import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateSingleTask } from "@/hooks/useCleaningTasks";
import { z } from "zod";

interface AddManualReservationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  defaultChecklistId?: string | null;
  availableCleaners?: Array<{ id: string; name: string }>;
}

const reservationSchema = z.object({
  guest_name: z.string().trim().max(100, "Guest name must be less than 100 characters").optional(),
  check_in: z.date({ required_error: "Check-in date is required" }),
  check_out: z.date({ required_error: "Check-out date is required" }),
  guest_count: z.number().int().min(1, "At least 1 guest").max(50, "Maximum 50 guests").optional(),
  notes: z.string().trim().max(500, "Notes must be less than 500 characters").optional(),
}).refine(data => data.check_out > data.check_in, {
  message: "Check-out must be after check-in",
  path: ["check_out"],
});

export const AddManualReservationModal = ({
  open,
  onOpenChange,
  propertyId,
  defaultChecklistId = null,
  availableCleaners = [],
}: AddManualReservationModalProps) => {
  const queryClient = useQueryClient();
  const createSingleTask = useCreateSingleTask();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [checkIn, setCheckIn] = useState<Date | undefined>();
  const [checkOut, setCheckOut] = useState<Date | undefined>();
  const [guestCount, setGuestCount] = useState("");
  const [notes, setNotes] = useState("");
  const [createTaskForCheckout, setCreateTaskForCheckout] = useState(true);
  const [assignCleanerId, setAssignCleanerId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setGuestName("");
    setCheckIn(undefined);
    setCheckOut(undefined);
    setGuestCount("");
    setNotes("");
    setCreateTaskForCheckout(true);
    setAssignCleanerId(null);
    setErrors({});
  };

  const handleSubmit = async () => {
    setErrors({});

    const validationResult = reservationSchema.safeParse({
      guest_name: guestName || undefined,
      check_in: checkIn,
      check_out: checkOut,
      guest_count: guestCount ? parseInt(guestCount, 10) : undefined,
      notes: notes || undefined,
    });

    if (!validationResult.success) {
      const fieldErrors: Record<string, string> = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const externalId = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const endDateStr = format(checkOut!, "yyyy-MM-dd");

      const { data: newReservation, error: insertError } = await supabase
        .from("reservations")
        .insert({
          property_id: propertyId,
          external_id: externalId,
          source: "manual",
          start_date: format(checkIn!, "yyyy-MM-dd"),
          end_date: endDateStr,
          guest_name: guestName.trim() || null,
          guest_count: guestCount ? parseInt(guestCount, 10) : null,
          notes: notes.trim() || null,
          is_manual: true,
          summary: guestName.trim() || "Manual reservation",
          created_by: user?.id ?? null,
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      if (!newReservation?.id) throw new Error("Reservation created but no id returned");

      if (createTaskForCheckout) {
        await createSingleTask.mutateAsync({
          propertyId,
          reservationId: newReservation.id,
          checklistId: defaultChecklistId,
          cleanerId: assignCleanerId,
          scheduledDate: endDateStr,
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] }),
      ]);

      toast({
        title: "Reservation added",
        description: createTaskForCheckout
          ? "Reservation and turnover cleaning task created."
          : "Reservation has been added.",
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error adding reservation:", error);
      toast({
        title: "Error",
        description: "Failed to add reservation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) resetForm();
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Reservation</DialogTitle>
          <DialogDescription>
            Add a reservation manually. Optionally create a turnover cleaning task for checkout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Guest Name */}
          <div className="space-y-2">
            <Label htmlFor="guest-name">Guest Name (optional)</Label>
            <Input
              id="guest-name"
              placeholder="e.g., John Smith"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              maxLength={100}
            />
            {errors.guest_name && (
              <p className="text-xs text-destructive">{errors.guest_name}</p>
            )}
          </div>

          {/* Check-in Date */}
          <div className="space-y-2">
            <Label>Check-in Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !checkIn && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkIn ? format(checkIn, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkIn}
                  onSelect={setCheckIn}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {errors.check_in && (
              <p className="text-xs text-destructive">{errors.check_in}</p>
            )}
          </div>

          {/* Check-out Date */}
          <div className="space-y-2">
            <Label>Check-out Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !checkOut && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkOut ? format(checkOut, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOut}
                  onSelect={setCheckOut}
                  disabled={(date) => checkIn ? date <= checkIn : false}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {errors.check_out && (
              <p className="text-xs text-destructive">{errors.check_out}</p>
            )}
          </div>

          {/* Guest Count */}
          <div className="space-y-2">
            <Label htmlFor="guest-count">Number of Guests (optional)</Label>
            <Input
              id="guest-count"
              type="number"
              placeholder="e.g., 4"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
              min={1}
              max={50}
            />
            {errors.guest_count && (
              <p className="text-xs text-destructive">{errors.guest_count}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details about this stay..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
            />
            {errors.notes && (
              <p className="text-xs text-destructive">{errors.notes}</p>
            )}
          </div>

          {/* Create cleaning task for checkout */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="create-task-toggle">Create cleaning task for checkout</Label>
              <p className="text-xs text-muted-foreground">
                Schedule a turnover cleaning task at checkout (default: Turnover cleaning).
              </p>
            </div>
            <Switch
              id="create-task-toggle"
              checked={createTaskForCheckout}
              onCheckedChange={setCreateTaskForCheckout}
            />
          </div>

          {createTaskForCheckout && availableCleaners.length > 0 && (
            <div className="space-y-2">
              <Label>Assign cleaner (optional)</Label>
              <Select
                value={assignCleanerId ?? "unassigned"}
                onValueChange={(v) => setAssignCleanerId(v === "unassigned" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Assign later" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Assign later</SelectItem>
                  {availableCleaners.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !checkIn || !checkOut}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Reservation"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};